import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowDown, ArrowUp, CreditCard, Check, Minus, Plus } from "lucide-react";
import QRCode from "react-qr-code";
import { SITE, STORY, PRIZES, DONORS } from "@/config";
import { createPurchase, createCheckoutSession } from "@/lib/api";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Purchase() {
  const [selected, setSelected] = useState(SITE.priceOptions[0].id);
  const [packs, setPacks] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
  });
  const [loading, setLoading] = useState(false);

  const option = SITE.priceOptions.find((o) => o.id === selected) || SITE.priceOptions[0];
  const totalTickets = option.tickets * packs;
  const total = (option.price * packs).toFixed(2);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and mobile.");
      return;
    }
    setLoading(true);
    try {
      const purchase = await createPurchase({
        name: form.name,
        email: form.email,
        phone: form.phone,
        option: option.id,
        packs,
        payment_method: "stripe",
      });
      const session = await createCheckoutSession(purchase.reference);
      window.location.assign(session.checkout_url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="purchase-page" className="min-h-screen bg-[#F5F5F5] px-6 py-8 md:py-12 flex flex-col items-center">
      {/* Hero photo */}
      <div data-testid="hero-photo-wrap" className="w-full max-w-2xl mb-8 rounded-2xl overflow-hidden border border-[#DDE1E4] bg-white">
        <img
          data-testid="hero-photo"
          src={SITE.heroPhoto}
          alt="The family we're rallying around"
          className="w-full max-h-[640px] object-contain bg-[#ECEFF1]"
        />
      </div>

      <h1 data-testid="page-title" className="font-serif-display text-4xl md:text-5xl text-[#333333] text-center max-w-2xl leading-tight mb-6">
        Help Give Mum More Tomorrows
      </h1>

      {/* Nav tabs — prominent rose-accented pills */}
      <div className="w-full max-w-2xl my-8 flex items-center gap-4">
        <div className="flex-1 h-px bg-[#DDE1E4]" />
        <button
          type="button"
          data-testid="nav-prizes"
          onClick={() => scrollTo("prizes")}
          className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full border-2 border-[#C77B7B]/60 bg-white shadow-[0_10px_30px_rgba(199,123,123,0.18)] hover:border-[#C77B7B] hover:shadow-[0_14px_36px_rgba(199,123,123,0.3)] hover:-translate-y-0.5 transition-all"
        >
          <span className="text-left">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[#C77B7B] font-semibold">See</span>
            <span className="block font-serif-display italic text-2xl leading-none text-[#333333] group-hover:text-[#C77B7B] transition-colors">Prizes</span>
          </span>
          <span className="w-9 h-9 rounded-full bg-[#C77B7B] grid place-items-center shrink-0 animate-[bounce_2s_ease-in-out_infinite]">
            <ArrowDown className="w-4 h-4 text-[#F5F5F5]" strokeWidth={2} />
          </span>
        </button>
        <button
          type="button"
          data-testid="nav-her-story"
          onClick={() => scrollTo("story")}
          className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full border-2 border-[#C77B7B]/60 bg-white shadow-[0_10px_30px_rgba(199,123,123,0.18)] hover:border-[#C77B7B] hover:shadow-[0_14px_36px_rgba(199,123,123,0.3)] hover:-translate-y-0.5 transition-all"
        >
          <span className="text-left">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[#C77B7B] font-semibold">Read</span>
            <span className="block font-serif-display italic text-2xl leading-none text-[#333333] group-hover:text-[#C77B7B] transition-colors">Her story</span>
          </span>
          <span className="w-9 h-9 rounded-full bg-[#C77B7B] grid place-items-center shrink-0 animate-[bounce_2s_ease-in-out_infinite]">
            <ArrowDown className="w-4 h-4 text-[#F5F5F5]" strokeWidth={2} />
          </span>
        </button>
        <div className="flex-1 h-px bg-[#DDE1E4]" />
      </div>

      <form
        id="donation"
        onSubmit={submit}
        data-testid="purchase-form"
        className="w-full max-w-2xl bg-white border border-[#DDE1E4] rounded-2xl p-8 md:p-10 space-y-8 scroll-mt-6"
      >
        <div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#333333]">{SITE.heading}</h2>
          <p className="mt-2 text-sm text-[#546E7A]">All fields required unless noted.</p>
        </div>

        {/* Ticket options */}
        <div>
          <p className="text-[11px] uppercase tracking-widest text-[#546E7A] mb-3">Choose your tickets</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SITE.priceOptions.map((o) => {
              const active = o.id === option.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  data-testid={`option-${o.id}`}
                  onClick={() => setSelected(o.id)}
                  className={`relative text-left p-5 rounded-xl border transition-colors ${
                    active
                      ? "border-[#C77B7B] bg-white shadow-sm"
                      : "border-[#DDE1E4] bg-[#ECEFF1] hover:border-[#C77B7B]"
                  }`}
                >
                  {o.tag && (
                    <span className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full bg-[#C77B7B] text-[#F5F5F5] text-[10px] uppercase tracking-widest">
                      {o.tag}
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <p className="text-[#333333] font-medium">{o.label}</p>
                    <span
                      className={`w-5 h-5 rounded-full border grid place-items-center transition-colors ${
                        active ? "border-[#C77B7B] bg-[#C77B7B]" : "border-[#DDE1E4] bg-white"
                      }`}
                    >
                      {active && <Check className="w-3 h-3 text-[#F5F5F5]" strokeWidth={2.5} />}
                    </span>
                  </div>
                  <p className="mt-2 font-serif-display text-3xl text-[#333333]">${o.price}</p>
                  <p className="text-xs text-[#546E7A] mt-1">
                    {o.tickets === 1 ? "1 entry in the draw" : `${o.tickets} entries in the draw`}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* How many */}
        <div>
          <p className="text-[11px] uppercase tracking-widest text-[#546E7A] mb-3">
            {option.id === "bundle" ? "How many bundles?" : "How many tickets?"}
          </p>
          <div className="flex items-center justify-between p-4 rounded-xl bg-[#ECEFF1] border border-[#DDE1E4]">
            <p data-testid="entries-note" className="text-sm text-[#546E7A]">
              {totalTickets} entr{totalTickets === 1 ? "y" : "ies"} in the draw
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="packs-decrease"
                onClick={() => setPacks((p) => Math.max(1, p - 1))}
                className="w-9 h-9 rounded-full border border-[#DDE1E4] bg-white grid place-items-center hover:border-[#C77B7B] transition-colors"
              >
                <Minus className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <span data-testid="packs-value" className="font-serif-display text-2xl w-8 text-center">{packs}</span>
              <button
                type="button"
                data-testid="packs-increase"
                onClick={() => setPacks((p) => Math.min(50, p + 1))}
                className="w-9 h-9 rounded-full border border-[#DDE1E4] bg-white grid place-items-center hover:border-[#C77B7B] transition-colors"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>

        {/* Personal details */}
        <div className="space-y-4">
          <p className="text-[11px] uppercase tracking-widest text-[#546E7A]">Your details</p>
          {[
            { k: "name", label: "Full name", type: "text", testid: "input-name" },
            { k: "email", label: "Email address", type: "email", testid: "input-email" },
            { k: "phone", label: "Mobile number", type: "tel", testid: "input-phone" },
          ].map((f) => (
            <input
              key={f.k}
              data-testid={f.testid}
              type={f.type}
              placeholder={f.label}
              value={form[f.k]}
              onChange={update(f.k)}
              className="w-full bg-transparent border-b border-[#DDE1E4] py-3 text-[#333333] placeholder:text-[#A0AEC0] focus:border-[#C77B7B] focus:outline-none transition-colors"
            />
          ))}
        </div>

        {/* Payment info */}
        <div>
          <p className="text-[11px] uppercase tracking-widest text-[#546E7A] mb-3">Payment</p>
          <div data-testid="stripe-info" className="p-4 rounded-xl bg-[#ECEFF1] border border-[#DDE1E4] text-sm text-[#333333] space-y-2">
            <div className="flex items-center gap-2 text-[#333333]">
              <CreditCard className="w-4 h-4 text-[#C77B7B]" strokeWidth={1.5} />
              <span className="font-medium">Pay securely with your card.</span>
            </div>
            <p className="text-xs text-[#546E7A]">
              When you click <span className="font-medium text-[#333333]">Buy ticket</span> we'll take you to Stripe's secure checkout page to pay <span className="font-medium text-[#333333]">${total}</span>.
            </p>
            <p className="text-xs text-[#546E7A] pt-2 border-t border-[#DDE1E4]">
              Your ticket number is issued and emailed to you as soon as the payment is approved.
            </p>
          </div>
        </div>

        {/* Total + submit */}
        <div className="pt-6 border-t border-[#DDE1E4]">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] uppercase tracking-widest text-[#546E7A]">Total</span>
            <span data-testid="order-total" className="font-serif-display text-4xl text-[#333333]">${total}</span>
          </div>
          <button
            type="submit"
            data-testid="submit-btn"
            disabled={loading}
            className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-[#C77B7B] text-[#F5F5F5] py-3.5 font-medium hover:bg-[#A66666] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy ticket · $${total}`}
          </button>
        </div>
      </form>

      {/* Prizes */}
      <section
        id="prizes"
        data-testid="prizes-section"
        className="w-full max-w-2xl mt-20 mb-6 scroll-mt-6"
      >
        <p className="text-[11px] uppercase tracking-widest text-[#C77B7B] text-center">Prizes</p>
        <h2 className="mt-3 font-serif-display text-3xl md:text-4xl text-[#333333] text-center leading-tight">
          {PRIZES.intro}
        </h2>

        <div className="mt-10 rounded-2xl overflow-hidden border border-[#DDE1E4] bg-white">
          <img
            data-testid="prizes-photo"
            src={PRIZES.groupPhoto}
            alt="The prizes on offer"
            className="w-full max-h-[520px] object-cover bg-[#ECEFF1]"
          />
        </div>

        <div className="mt-10 space-y-10">
          {PRIZES.items.map((p, i) => (
            <div key={i} data-testid={`prize-${p.place}`} className="border-t border-[#DDE1E4] pt-8">
              <div className="flex items-center gap-5">
                <span className="font-serif-display italic text-5xl md:text-6xl text-[#C77B7B] leading-none min-w-[4.5rem]">
                  {p.place}
                </span>
                <div className="flex-1">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <h3 className="font-serif-display text-2xl md:text-3xl text-[#333333]">{p.title}</h3>
                    <span className="text-xs uppercase tracking-widest text-[#546E7A]">{p.value}</span>
                  </div>
                </div>
              </div>
              <div className={`mt-6 grid gap-3 ${p.contents.length > 2 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
                {p.contents.map((c, j) => (
                  <div
                    key={j}
                    data-testid={`prize-${p.place}-item-${j + 1}`}
                    className="group p-4 md:p-5 rounded-xl bg-white border border-[#DDE1E4] hover:border-[#C77B7B] transition-colors"
                  >
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#C77B7B] mb-1.5">Prize {j + 1}</p>
                    <p className="text-[#333333] text-sm md:text-base font-medium leading-snug">{c}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Her story */}
      <section
        id="story"
        data-testid="story-section"
        className="w-full max-w-2xl mt-20 mb-6 scroll-mt-6"
      >
        <p className="text-[11px] uppercase tracking-widest text-[#C77B7B] text-center">Her story</p>
        <h2 className="mt-3 font-serif-display text-3xl md:text-4xl text-[#333333] text-center leading-tight">
          {STORY.intro}
        </h2>

        <div className="mt-12 space-y-10">
          {STORY.chapters.map((c, i) => (
            <div key={i} className="border-t border-[#DDE1E4] pt-8">
              <p className="text-[11px] uppercase tracking-widest text-[#546E7A]">{c.label}</p>
              <h3 className="mt-2 font-serif-display text-2xl md:text-3xl text-[#333333]">{c.title}</h3>
              <p className="mt-4 text-[#333333] text-base leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 p-8 md:p-10 rounded-2xl bg-white border border-[#DDE1E4] text-center">
          <p className="font-serif-display text-xl md:text-2xl text-[#333333] leading-relaxed">
            {STORY.outro}
          </p>
          <p className="mt-4 text-sm text-[#546E7A]">{STORY.signoff}</p>

          <button
            type="button"
            data-testid="nav-donation-details"
            onClick={() => scrollTo("donation")}
            className="group inline-flex items-center gap-3 mt-8 px-7 py-3.5 rounded-full bg-[#C77B7B] text-[#F5F5F5] hover:bg-[#A66666] transition-colors"
          >
            <ArrowUp className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" strokeWidth={1.5} />
            <span className="font-serif-display italic text-xl">Donation details</span>
          </button>
        </div>

        {/* Donor acknowledgements */}
        <div data-testid="donors-section" className="mt-8 p-8 md:p-10 rounded-2xl bg-white border border-[#DDE1E4] text-center">
          <p className="text-[11px] uppercase tracking-widest text-[#C77B7B]">{DONORS.heading}</p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4 items-stretch">
            {DONORS.logos.map((d) => (
              <div
                key={d.name}
                data-testid={`donor-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex items-center justify-center p-4 rounded-xl border transition-colors ${
                  d.dark ? "border-transparent" : "border-[#DDE1E4] bg-white"
                }`}
                style={d.dark ? { backgroundColor: "#6f7072" } : undefined}
              >
                <img
                  src={d.src}
                  alt={d.name}
                  className="max-h-16 w-auto object-contain"
                />
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#546E7A]">{DONORS.extra}</p>
        </div>

        {/* Share QR — encodes whatever address the site is served from */}
        <div data-testid="share-qr" className="mt-8 p-8 md:p-10 rounded-2xl bg-white border border-[#DDE1E4] text-center flex flex-col items-center">
          <p className="text-[11px] uppercase tracking-widest text-[#C77B7B]">Share this page</p>
          <div className="mt-5 p-4 rounded-xl bg-white border border-[#DDE1E4]">
            <QRCode
              data-testid="share-qr-code"
              value={window.location.origin}
              size={128}
              bgColor="#FFFFFF"
              fgColor="#333333"
            />
          </div>
          <p className="mt-4 text-sm text-[#546E7A]">Scan with a phone camera to open the ticket page.</p>
        </div>
      </section>
    </div>
  );
}
